import React, { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import moment from 'moment';
import { DatePicker, Icon, Tooltip } from 'fpay-ui';
import styled, { css } from 'styled-components';
import Grid from '@material-ui/core/Grid';
import InputText from '@material-ui/core/OutlinedInput';
import InputAdornment from '@material-ui/core/InputAdornment';

import { useTransaction, useAuth, useCommon, useConfig } from '@hooks';
import { ConfirmationModal, TooltipToModal } from '@components';
import { dateDifference, getFormatedDate, getInitialFilter, getLimitDays } from '@helpers';
import { REPORT_TRANSACTIONS_LIMIT } from '@constants/transactions';

import { getEnv } from '@config';

const { publicPath } = getEnv();

const ContainerFilters = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  ${({ theme }) => theme.breakpoint('md')} {
    flex-direction: row;
  }
`;

const ContainerDatePicker = styled.div`
  display: flex;
  width: 100%;
`;
const ContainerInput = styled.div`
  display: flex;
  width: 100%;
  ${({ theme }) => theme.breakpoint('md')} {
    max-width: 277px;
  }
`;

const StyledDatePicker = styled.div`
  min-height: 36px;
  width: 0px;
  #btn-drop-down-calendar {
    display: none;
  }

  > div:first-child {
    ::before {
      display: none;
      ${({ theme }) => theme.breakpoint(null, 'md')} {
        display: ${({ show }) => (show ? 'unset' : 'none')};
      }
      content: '';
      background-color: #444b62;
      position: fixed;
      left: 0;
      top: 0;
      height: 100%;
      width: 100%;
      z-index: -1;
      opacity: 70%;
      transition: opacity 1s ease-out;
    }
    ${({ theme }) => theme.breakpoint('sm', 'md')} {
      > div {
        bottom: unset;
        border-radius: 5px;
        margin: 24px;
        > div:nth-child(3) {
          display: none;
        }
        > div:nth-child(4) {
          padding-top: 16px;
        }
      }
    }
  }
`;

const StyledFilter = styled.div`
  display: flex;
  justify-content: flex-start;
  margin-top: 16px;
  align-items: center;
  width: 100%;
  height: 40px;
  border-radius: 4px;
  border: solid 1px #caced8;
  ${({ theme }) => theme.breakpoint('md')} {
    min-width: 300px;
    width: auto;
    margin-top: 0;
    margin-left: 20px;
  }
`;

const SyledDateContainer = styled.div`
  display: flex;
  min-width: 200px;
  cursor: pointer;
  margin-left: 8px;
  margin-right: 8px;
  justify-content: flex-start;
  width: 100%;
`;

const StyledDateFilter = styled.div`
  width: 100%;
  font-size: 14px;
  ${({ theme }) => theme.newBreakpoint('sm')} {
    font-size: 12px;
  }
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 2;
  letter-spacing: normal;
  text-align: left;
  color: var(--light_grey_blue);
  padding-left: 16px;
  padding-right: 16px;
`;
const StyledInputText = styled(InputText)`
  ${({ theme }) => theme.newBreakpoint('sm')} {
    width: 100%;
  }
  #input-buscar-qr-id-monto {
    padding: 10.5px 10.5px 10.5px 0;
  }
`;

const ExportSpinner = styled.span`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #d0e4f7;
  border-top-color: #0072ce;
  border-radius: 50%;
  animation: exportSpin 0.8s linear infinite;
  @keyframes exportSpin {
    to {
      transform: rotate(360deg);
    }
  }
`;

const ReportButton = styled.button`
  display: flex;
  align-items: center;
  border: none;
  background: none;
  margin-left: 8px;
  padding: 0;
  ${({ loading }) =>
    loading &&
    css`
      cursor: not-allowed;
      pointer-events: none;
    `}
`;

const TextoFpay = styled.div(
  ({ theme }) => css`
    text-align: center;
    font-size: 0.75rem;
    letter-spacing: 0px;
    color: ${theme.colors.gray800};
    opacity: 1;
    line-height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
  `
);

const StyledDatePickerHeader = styled.div(
  ({ theme }) => css`
    margin-bottom: 16px;
    background-color: #ffeebf;
    display: flex;
    align-items: center;
    > div {
      display: flex;
      &:first-child {
        padding: 14px 2px 14px 14px;
      }
      > svg {
        path:nth-child(2) {
          fill: #8a670f;
        }
        fill: #8a670f;
        transform: scale(1.1);
      }
      color: #8a670f;
      font-weight: 600;
      padding: 10px 12px;
      font-size: 12px;
    }
    ${() => theme.breakpoint(null, 'md')} {
      margin: 24px 24px 0px 24px;
    }
  `
);

const StyledInputAdornment = styled(InputAdornment)`
  cursor: pointer;
  opacity: ${({ hidden }) => (hidden ? '0' : '1')};
`;

const DatePickerTitle = styled.div(
  ({ theme }) => css`
    display: none;
    ${() => theme.breakpoint(null, 'md')} {
      display: unset;
    }
    margin-left: 24px;
    margin-top: 16px;
    font-weight: 600;
  `
);

const IconButton = styled.button`
  border: none;
  background: none;
  padding: 0;
  & svg {
    vertical-align: middle;
  }
`;

const StyledTooltip = styled(TooltipToModal)`
  & svg {
    margin-left: 2px;
    margin-right: 2px;
    width: 20px;
    height: 20px;
  }
  & > div {
    display: flex;
    align-items: center;
  }
`;

export default function ({ falabella, htmlIdReportButton = 'unidentified-id' }) {
  const { config } = useConfig();
  const { updateFilter, exportTransactionsCsv, resetPagination, filter, isLoadingExport } = useTransaction();
  const { t } = useTranslation();
  const { isAllowed, merchant } = useAuth();
  const [showCalendar, setShowCalendar] = useState(false);
  const { setModal } = useCommon();
  const { startDate, endDate, text, filterApply } = filter;

  const {
    merchant: { created_at: createdAt },
  } = useSelector((state) => state.login);

  const onKeyDown = (event) => {
    if (event.keyCode === 13 /** Enter */) {
      resetPagination();
    }
  };

  const onTextChange = (e) => {
    const { target } = e;
    updateFilter({
      text: target.value,
      startDate: null,
      endDate: null,
      filterApply: true,
    });
  };

  const handleReset = () => {
    // TODO: se puede agregar una funcion resetFilter
    const initialFilter = getInitialFilter(merchant, config);
    updateFilter(initialFilter);
    resetPagination();
  };

  // eslint-disable-next-line no-shadow
  const onDateChange = (startDate, endDate) => {
    updateFilter({
      text: null,
      startDate,
      endDate,
      filterApply: true,
    });
    resetPagination();
  };

  const handleDownload = () => {
    setModal({
      body: (
        <ConfirmationModal
          htmlIdCancelButton="cancel-transaction-report"
          htmlIdConfirmButton="confirm-transaction-report"
          title={t('transactions.report.confirmModal')}
          handleConfirm={exportTransactionsCsv}
        />
      ),
    });
  };

  const limitDays = useMemo(() => getLimitDays(merchant, config), [merchant, config]);

  const datePickerHeader = (
    <>
      <StyledDatePickerHeader>
        <div>
          <Icon icon="ic-warning" color="#8A670F" fill="#8A670F" />
        </div>
        <div>{t('transactions.datePicker.header', { limitDays })}</div>
      </StyledDatePickerHeader>
      <DatePickerTitle>{t('transactions.datePicker.title')}</DatePickerTitle>
    </>
  );

  const getEndAdornment = () => {
    if (text) {
      return (
        <StyledInputAdornment position="end" onClick={handleReset}>
          <img src={`${publicPath}/ic_close_circle_24_available.svg`} alt="limpiar filtro" />
        </StyledInputAdornment>
      );
    }
    return (
      <StyledInputAdornment position="end">
        <StyledTooltip
          iconName="alert"
          tooltipText={
            falabella
              ? t('transactionsFalabella.tooltipInput')
              : t('transactions.filter.saleAuthorizationId.tooltip')
          }
        />
      </StyledInputAdornment>
    );
  };

  return (
    <>
      <ContainerFilters>
        <ContainerInput>
          <StyledInputText
            className="inputBuscar"
            type="text"
            id="input-buscar-qr-id-monto"
            onKeyDown={onKeyDown}
            onChange={onTextChange}
            value={text || ''}
            placeholder={
              falabella
                ? t('transactionsFalabella.input')
                : t('transactions.filter.saleAuthorizationId.placeholder')
            }
            startAdornment={
              <InputAdornment position="start">
                <img src={`${publicPath}/lupa.svg`} alt="imagen de lupa" />
              </InputAdornment>
            }
            endAdornment={getEndAdornment()}
          />
        </ContainerInput>
        <ContainerDatePicker>
          <StyledFilter>
            <SyledDateContainer>
              <IconButton onClick={() => setShowCalendar((state) => !state)}>
                <Icon icon="ic-calendar" />
              </IconButton>
              <StyledDateFilter type="button" onClick={() => setShowCalendar((state) => !state)}>
                {filterApply && startDate && endDate
                  ? `${getFormatedDate(startDate, 'dd/MM/yyyy')} a ${getFormatedDate(
                      endDate,
                      'dd/MM/yyyy'
                    )}`
                  : t('transactions.report.selectDate')}
              </StyledDateFilter>
              {filterApply && startDate && (
                <IconButton onClick={handleReset}>
                  <Icon id="img-cerrar-x" icon="ic-close-circle" />
                </IconButton>
              )}
              {isAllowed('REPORT_TRANSACTIONS') &&
                (!!text || dateDifference(startDate, endDate) < REPORT_TRANSACTIONS_LIMIT ? (
                  <ReportButton
                    id={htmlIdReportButton}
                    className="noLarge"
                    onClick={!isLoadingExport ? handleDownload : undefined}
                    loading={isLoadingExport ? 1 : 0}
                    data-testid="download-report-transactions"
                  >
                    {isLoadingExport ? <ExportSpinner /> : <Icon icon="ic-download" />}
                  </ReportButton>
                ) : (
                  <Tooltip
                    position="bottom-right"
                    duration="200"
                    width="11.875rem"
                    height="3.125rem"
                    cssPointers
                  >
                    <ReportButton className="noLarge">
                      <Icon icon="ic-download-disabled" />
                    </ReportButton>
                    <TextoFpay>
                      {t('transactions.report.reportLimit', {
                        limitDays: REPORT_TRANSACTIONS_LIMIT,
                      })}
                    </TextoFpay>
                  </Tooltip>
                ))}
            </SyledDateContainer>
          </StyledFilter>
          <StyledDatePicker show={showCalendar}>
            <DatePicker
              show={showCalendar}
              setShow={setShowCalendar}
              onChange={({ init, finish }) => onDateChange(init, finish)}
              intervalDateSelect={{
                init: filterApply ? startDate : undefined,
                finish: filterApply ? endDate : undefined,
              }}
              placeholder="Seleccionar fecha"
              minDate={moment(createdAt).toDate()}
              maxDate={moment().toDate()}
              limitDays={limitDays}
              header={datePickerHeader}
            />
          </StyledDatePicker>

          <Grid item className="justLarge">
            {isAllowed('REPORT_TRANSACTIONS') &&
              (!!text || dateDifference(startDate, endDate) < REPORT_TRANSACTIONS_LIMIT ? (
                <ReportButton
                  id={htmlIdReportButton}
                  onClick={!isLoadingExport ? handleDownload : undefined}
                  loading={isLoadingExport ? 1 : 0}
                  data-testid="download-report-transactions"
                >
                  {isLoadingExport ? <ExportSpinner /> : <Icon icon="ic-download" />}
                </ReportButton>
              ) : (
                <Tooltip
                  position="bottom-right"
                  duration="200"
                  width="11.875rem"
                  height="3.125rem"
                  cssPointers
                >
                  <ReportButton>
                    <Icon icon="ic-download-disabled" />
                  </ReportButton>
                  <TextoFpay>
                    {t('transactions.report.reportLimit', { limitDays: REPORT_TRANSACTIONS_LIMIT })}
                  </TextoFpay>
                </Tooltip>
              ))}
          </Grid>
        </ContainerDatePicker>
      </ContainerFilters>
    </>
  );
}
